/**
 * Session lifecycle config — idle timeout + heartbeat (PR-C, 2026-07-20).
 *
 * Single source of truth for the timing constants used by IdleService and
 * HeartbeatService. Every value lives here so it's never hardcoded twice —
 * DEFER_RECHECK-style drift between "what the doc says" and "what the code
 * does" has caused real incidents in this app (see VersionWatchdogService).
 */

/** User activity (pointerdown/keydown/scroll/visibilitychange) resets this
 *  timer. At the end of it, IdleService shows the "Still there?" warning
 *  rather than signing out immediately — see IDLE_WARNING_MS below. */
export const IDLE_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours

/**
 * Length of the "Still there?" countdown shown once IDLE_TIMEOUT_MS elapses,
 * before a forced sign-out. 60 seconds: long enough for someone who stepped
 * away to notice a modal and react (WCAG 2.2 SC 2.2.1 wants at least 20s of
 * warning with a way to extend — this is well past that floor), short
 * enough that a truly-idle tab doesn't sit signed-in for an extra hour once
 * the 4h mark is hit. Matches the "short countdown" the brief asked for
 * without inventing a second config knob for it.
 */
export const IDLE_WARNING_MS = 60 * 1000; // 60 seconds

/** How often HeartbeatService polls the lightweight auth-check endpoint
 *  while the tab is visible. 5 minutes: frequent enough to catch a
 *  server-revoked session well within one working session, cheap enough
 *  (a single /auth/me GET) not to matter at fleet scale. */
export const HEARTBEAT_POLL_MS = 5 * 60 * 1000; // 5 minutes

/** Query param name used to carry a post-login return destination through
 *  /login. Matches the existing convention (see
 *  verify-listing.component.ts's `router.navigate(['/login'], { queryParams:
 *  { returnUrl: ... } })`) — reused verbatim, not reinvented. */
export const RETURN_URL_PARAM = 'returnUrl';

/** Backend error code (PR-B, lp-vx, not yet merged as of this PR) for a
 *  refresh rejected because the session exceeded its 48h absolute age cap.
 *  Read defensively (optional chaining, tolerate absence) everywhere this
 *  is checked — PR-B may deploy after this PR, or ship in a different shape. */
export const SESSION_MAX_AGE_EXCEEDED_CODE = 'session_max_age_exceeded';

export const SESSION_MAX_AGE_MESSAGE = 'For security, please sign in again.';
